import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { get } from 'lodash';

// import { ModalNameEnum } from '../../../../models/modal-name.enum';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { SalesOrderApiService } from 'projects/workspace/src/app/sales-order/services/sales-order-api.service';
import { CustomerTypeEnum, UIStatesEnum } from '../../../../models';
import { UpdatePropertiesBlockValid } from 'projects/workspace/src/app/sales-order/store/actions/sales-order.actions';
import { FormInputChangedModel } from 'projects/workspace/src/app/shared/models';
import { EcoOrderModel, EcoOrderPropertiesModel } from 'projects/workspace/src/app/e-commerce/modules/eco-orders/models';
import {
  selectEcoOrder,
  selectEcoOrderCurrentState, selectEcoOrderPdfSettings
} from 'projects/workspace/src/app/e-commerce/modules/eco-orders/store/selectors';
import { EcoOrderListTabsEnum } from 'projects/workspace/src/app/e-commerce/modules/eco-orders/enums';
import { PdfSettingsModel } from 'projects/workspace/src/app/pdf-settings/models';

@Component({
  selector: 'rnpl-eco-add-partner-modal',
  templateUrl: './eco-add-partner-modal.component.html',
})
export class EcoAddPartnerModalComponent implements OnInit, OnDestroy {

  public pdfSettings: PdfSettingsModel;
  public ecoOrder: EcoOrderModel;
  public form: FormGroup;
  public isReadonly: boolean = false;
  readonly destroy$: Subject<void> = new Subject();

  // public modalNameEnum: typeof ModalNameEnum = ModalNameEnum;
  // public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    public dialogRef: MatDialogRef<EcoAddPartnerModalComponent>,
    private store: Store<AppState>,
    private salesOrderApiService: SalesOrderApiService,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });
  }

  ngOnInit() {
    this.trackSalesOrderChanges();
    this.trackSalesOrderStateChanges();

    this.store.select(selectEcoOrderPdfSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((pdfSettings: PdfSettingsModel) => {
        this.pdfSettings = pdfSettings;
      });
  }

  private trackSalesOrderChanges(): void {
    this.store.select(selectEcoOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ecoOrder: EcoOrderModel) => {
        this.ecoOrder = ecoOrder;
        this.initForm(ecoOrder.properties);
      });
  }
  public initForm(properties: EcoOrderPropertiesModel = {} as EcoOrderPropertiesModel): void {
    this.form = this.fb.group({
      partnerType: [get(properties, 'partnerType'), [Validators.required]],
      companyId: [get(properties, 'company.id'), [Validators.required]],
      companyContactId: [get(properties, 'companyContact.id')],
      companyContactTitle: [get(properties, 'companyContact.title')],
      companyContactFirstName: [get(properties, 'companyContact.firstName')],
      companyContactLastName: [get(properties, 'companyContact.lastName')],
      email: [get(properties, 'email')],
      phone: [get(properties, 'phone')],
    });

    if (this.partnerType.value === CustomerTypeEnum.CORPORATE_PARTNER) {
      this.companyContactId.setValidators(Validators.required);
    }

    this.cdr.detectChanges();
    this.store.dispatch(UpdatePropertiesBlockValid({ propertiesBlockValid: this.form.valid }));
  }

  public updatePartnerField(partnerData): void {
    this.salesOrderApiService.updateSalesOrder(
      this.ecoOrder.id,
      {
        fieldValue: partnerData.partnerType,
        fieldName: 'properties.' + partnerData.partnerTypeFieldName
      }).subscribe(); // updated via store
  }

  public updateProperties(field: FormInputChangedModel): void {
    if (field.fieldName === 'contactTitle') { field.fieldName = 'companyContact.title'; }
    if (field.fieldName === 'contactFirstName') { field.fieldName = 'companyContact.firstName'; }
    if (field.fieldName === 'contactLastName') { field.fieldName = 'companyContact.lastName'; }

    field.fieldName = 'properties.' + field.fieldName;
    this.salesOrderApiService.updateSalesOrder(this.ecoOrder.id, field)
      .pipe(takeUntil(this.destroy$))
      .subscribe(); // updated via store
  }

  private trackSalesOrderStateChanges(): void {
    this.store.select(selectEcoOrderCurrentState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: UIStatesEnum) => {
        this.isReadonly = state === UIStatesEnum.VIEW;
        this.setFormsState();
      });
  }

  private setFormsState(): void {
    const opts = { onlySelf: true, emitEvent: false };
    if (this.isReadonly) {
      this.form.disable(opts);
    } else {
      this.form.enable(opts);
      this.cdr.detectChanges();
    }

    if (this.ecoOrder.status === EcoOrderListTabsEnum.OPEN) {
      this.form.get('partnerType').disable(opts);
      this.form.get('companyId').disable(opts);
    }
  }

  get isCorporatePartner(): boolean {
    return get(this.ecoOrder, 'properties.partnerType') === CustomerTypeEnum.CORPORATE_PARTNER;
  }

  get udfContact(): boolean {
    return get(this.ecoOrder, 'properties.companyContact.type') === 'user_defined';
  }

  get email(): FormControl { return this.form.get('email') as FormControl; }
  get phone(): FormControl { return this.form.get('phone') as FormControl; }
  get companyId(): FormControl { return this.form.get('companyId') as FormControl; }
  get partnerType(): FormControl { return this.form.get('partnerType') as FormControl; }
  get companyContactId(): FormControl { return this.form.get('companyContactId') as FormControl; }
  get companyContactTitle(): FormControl { return this.form.get('companyContactTitle') as FormControl; }
  get companyContactFirstName(): FormControl { return this.form.get('companyContactFirstName') as FormControl; }
  get companyContactLastName(): FormControl { return this.form.get('companyContactLastName') as FormControl; }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
